import { yupResolver } from '@hookform/resolvers/yup';
import {
  Grid,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  styled,
} from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { FieldValues, useForm, FormProvider } from 'react-hook-form';
import {
  IWorkspace,
  fetchJobBoardByWorkspaceId,
  IJobBoardPayload,
  addJobBoardAccounts,
  deleteJobBoardAccounts,
} from 'wisestep-api';
import {
  WsDrawer,
  WsColors,
  WsTypoVariants,
  WsTypography,
  WsButton,
  ControlledTextInput,
} from 'wisestep-base';
import { object, string } from 'yup';

import { success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { DeleteIconButton } from '../../../crmSettings/components/StyledIconButtons';

export interface drawerProps {
  openDrawer: boolean;
  workspace: IWorkspace | null;
  onClose(): void;
}

const CustomTableHeadCell = styled(TableCell)(() => ({
  paddingLeft: 32,
  fontWeight: 600,
  fontSize: '14px',
  color: WsColors.TertiaryShades600,
  cursor: 'pointer',
}));

interface IViewProps {
  accountId: string;
  accountName: string;
  catToken: string;
  workspaceId: string;
  createdBy: string;
}

const validationSchema = object().shape({
  accountName: string().required('Username is required'),
  catID: string().min(4, 'Token must be at least 4 characters').required('Token is required'),
});

const ConfigureMonsterAccountDrawer = ({ openDrawer, workspace, onClose }: drawerProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);

  const DefaultValues = {
    accountName: '',
    catID: '',
    workspaceId: '',
    createdBy: '',
  };

  const formOptions = {
    defaultValues: DefaultValues,
    resolver: yupResolver(validationSchema),
  };

  const methods = useForm(formOptions);
  const { handleSubmit } = methods;
  const [viewChecker, setViewChecker] = useState<IViewProps[]>([]);

  const onSaveHandler = async (data: FieldValues) => {
    try {
      const payload: IJobBoardPayload = {
        jobBoardName: 'MONSTER',
        username: data.accountName,
        password: data.catID,
        workspaceId: workspace?.id,
        createdBy: workspace?.ownerName,
      };
      await addJobBoardAccounts(payload);
      setNotification(success('Account has been saved Successfully !!'));
      onClose();
    } catch {
      setNotification(failed(''));
    }
  };

  const onDeleteHandler = async (id: string) => {
    try {
      await deleteJobBoardAccounts(id);
      setNotification(success('Deleted Successfully.'));
      onClose();
      syncJobBoardByWorkspace();
    } catch (error) {
      setNotification(failed('Something went wrong, Please try again.'));
    }
  };

  const getTruncatedToken = (token: string) => {
    return token.length > 10 ? token.substring(0, 9) : token;
  };

  const syncJobBoardByWorkspace = async () => {
    if (workspace) {
      setViewChecker(await updateData(workspace));
    }
  };

  useEffect(() => {
    const fetchJobBoardByWorkspace = async () => {
      if (workspace) {
        setViewChecker(await updateData(workspace));
      }
    };
    fetchJobBoardByWorkspace();
  }, [workspace, setViewChecker]);

  return (
    <WsDrawer
      size="medium"
      position="right"
      iconType="arrow"
      open={openDrawer}
      onClose={onClose}
      title="Configure Monster Account"
      headerSize="medium"
    >
      <FormProvider {...methods}>
        <Grid container p={3} spacing={2}>
          <Grid item xs={12} pt={2}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              Configure Your Monster Account for Workspace :{' '}
              <b>{workspace ? workspace.name : ''}</b>
            </WsTypography>
          </Grid>
          <Grid item xs={7}>
            <ControlledTextInput
              name="accountName"
              labelText="Account Name"
              type="text"
              fullWidth
              placeholder="Account Name"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={7}>
            <ControlledTextInput
              name="catID"
              labelText="CAT Id"
              type="text"
              fullWidth
              placeholder="Enter your CAT Id"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <Stack direction="row" spacing={2} pt={2}>
              <WsButton type="submit" onClick={handleSubmit(onSaveHandler)}>
                Save
              </WsButton>
              <WsButton onClick={onClose}>Cancel</WsButton>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <CustomTableHeadCell align="left">Account Name</CustomTableHeadCell>
                  <CustomTableHeadCell colSpan={2} align="left">
                    CAT ID
                  </CustomTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewChecker.map((item, index) => (
                  <Fragment key={index}>
                    <TableRow>
                      <TableCell align="left">
                        <b>{item.accountName}</b>
                      </TableCell>
                      <TableCell align="left">{getTruncatedToken(item.catToken)}</TableCell>
                      <TableCell>
                        <DeleteIconButton
                          onClick={() => onDeleteHandler(item.accountId)}
                        ></DeleteIconButton>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </FormProvider>
    </WsDrawer>
  );
};
async function updateData(workspace?: IWorkspace) {
  const workspaceId = workspace?.id ?? '';
  const response = await fetchJobBoardByWorkspaceId('MONSTER', workspaceId);
  const viewData = response.data.map((account) => {
    return {
      accountId: account?.id,
      accountName: account?.username,
      catToken: account.password,
      workspaceId: workspaceId,
      createdBy: workspace?.ownerName,
    };
  });
  return viewData;
}
export default ConfigureMonsterAccountDrawer;
